// frontend/src/pages/SettingsPage/components/IntegrationsMainContent.tsx
import { Info, LayoutGrid, List, Search } from "lucide-react";
import React, { useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { useNavigate } from "react-router";
import { cn } from "src/common/utils";
import CustomMcpServerCard from "src/components/integrations/CustomMcpServerCard";
import { getIntegrationCategories, INTEGRATION_INFO, IntegrationType } from "src/components/integrations/integration-categories";
import IntegrationCard from "src/components/integrations/IntegrationCard";
import IntegrationCategoryCard from "src/components/integrations/IntegrationCategoryCard";
import { Input } from "src/components/ui/input";
import { Tooltip } from "src/components/ui/tooltip";
import { usePermissions } from "src/context/MeContext";
import { getProductName } from "src/utils/brandTheme";

import { IntegrationsMainContent_organization$key } from "./__generated__/IntegrationsMainContent_organization.graphql";

const integrationsMainContentFragment = graphql`
  fragment IntegrationsMainContent_organization on GraphQLOrganization {
    id
    brandTheme
    integrations {
      edges {
        node {
          id
          integrationType
        }
      }
    }
    activeCustomMcpServers {
      id
      name
      slug
      description
      isActive
    }
  }
`;

interface IntegrationsMainContentProps {
  organization: IntegrationsMainContent_organization$key;
}

type McpServer = {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  isActive: boolean;
};

const ReadOnlyIntegrationsBanner: React.FC = () => {
  const { canWrite } = usePermissions();
  const canModify = canWrite("integrations");

  if (canModify) {
    return null;
  }

  return (
    <div className="mb-6 flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/10 p-4">
      <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm text-primary">
          You have read-only access to integrations. Contact an admin to make changes.
        </p>
      </div>
    </div>
  );
};

export default function IntegrationsMainContent({ organization: organizationKey }: IntegrationsMainContentProps) {
  const organization = useFragment(integrationsMainContentFragment, organizationKey);
  const { isSuperAdmin } = usePermissions();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"categories" | "all">("categories");

  const brandTheme = organization.brandTheme;
  const productName = getProductName(brandTheme);

  const connectedTypes = useMemo(
    () => (organization.integrations?.edges ?? [])
      .map(edge => edge?.node?.integrationType)
      .filter(Boolean) as string[],
    [organization.integrations?.edges],
  );
  const connectedTypeSet = useMemo(() => new Set(connectedTypes), [connectedTypes]);

  const mcpServers: McpServer[] = useMemo(
    () => (organization.activeCustomMcpServers ?? []).map(s => ({
      id: s.id,
      name: s.name,
      slug: s.slug,
      description: s.description,
      isActive: s.isActive,
    })),
    [organization.activeCustomMcpServers],
  );
  const customMcpServerCount = mcpServers.length;

  const categories = useMemo(
    () => getIntegrationCategories(productName, brandTheme, isSuperAdmin),
    [productName, brandTheme, isSuperAdmin],
  );

  const allIntegrations = useMemo(() => {
    const seen = new Set<IntegrationType>();
    const items: { type: IntegrationType; categorySlug: string }[] = [];
    for (const category of categories) {
      for (const type of category.integrationTypes) {
        if (seen.has(type)) continue;
        seen.add(type);
        items.push({ type, categorySlug: category.urlSlug });
      }
    }
    return items;
  }, [categories]);

  const trimmedQuery = searchQuery.trim().toLowerCase();
  const isSearching = trimmedQuery.length > 0;
  const showFlatView = isSearching || viewMode === "all";

  type FlatItem =
    | { kind: "integration"; name: string; isConnected: boolean; type: IntegrationType; categorySlug: string }
    | { kind: "mcp-server"; name: string; isConnected: boolean; server: McpServer }
    | { kind: "custom-mcp"; name: string; isConnected: boolean };

  const { connectedItems, disconnectedItems } = useMemo(() => {
    if (!showFlatView) return { connectedItems: [] as FlatItem[], disconnectedItems: [] as FlatItem[] };
    const items: FlatItem[] = [];

    for (const { type, categorySlug } of allIntegrations) {
      const info = INTEGRATION_INFO[type];
      if (!info) continue;
      if (isSearching) {
        const haystack = `${info.name} ${info.description}`.toLowerCase();
        if (!haystack.includes(trimmedQuery)) continue;
      }
      items.push({
        kind: "integration",
        name: info.name,
        isConnected: connectedTypeSet.has(type),
        type,
        categorySlug,
      });
    }

    for (const server of mcpServers) {
      if (isSearching) {
        const haystack = `${server.name} ${server.description ?? ""}`.toLowerCase();
        if (!haystack.includes(trimmedQuery)) continue;
      }
      items.push({ kind: "mcp-server", name: server.name, isConnected: true, server });
    }

    if (!isSearching && viewMode === "all") {
      items.push({ kind: "custom-mcp", name: "Custom MCP", isConnected: customMcpServerCount > 0 });
    }

    const byName = (a: FlatItem, b: FlatItem) => a.name.localeCompare(b.name);
    return {
      connectedItems: items.filter(i => i.isConnected).sort(byName),
      disconnectedItems: items.filter(i => !i.isConnected).sort(byName),
    };
  }, [showFlatView, allIntegrations, mcpServers, isSearching, trimmedQuery, viewMode, connectedTypeSet, customMcpServerCount]);

  const flatItemCount = connectedItems.length + disconnectedItems.length;

  const handleIntegrationClick = (type: IntegrationType, categorySlug: string) => {
    navigate(`/settings/integrations/${categorySlug}?integration=${type}`);
  };

  const handleMcpServerClick = (serverId: string) => {
    navigate(`/settings/integrations/mcp-servers/${serverId}/edit`);
  };

  const handleCustomMcpClick = () => {
    navigate("/settings/integrations/mcp-servers");
  };

  const renderFlatItem = (item: FlatItem) => {
    if (item.kind === "integration") {
      return (
        <IntegrationCard
          key={`int-${item.type}`}
          integrationType={item.type}
          isConnected={connectedTypeSet.has(item.type)}
          onClick={() => handleIntegrationClick(item.type, item.categorySlug)}
        />
      );
    }
    if (item.kind === "mcp-server") {
      return (
        <CustomMcpServerCard
          key={`mcp-${item.server.id}`}
          name={item.server.name}
          description={item.server.description}
          isActive={item.server.isActive}
          onClick={() => handleMcpServerClick(item.server.id)}
        />
      );
    }
    return (
      <CustomMcpServerCard
        key="custom-mcp-entry"
        name="Custom MCP"
        statusLabel={customMcpServerCount === 0 ? "Not connected" : `${customMcpServerCount} connected`}
        actionLabel={customMcpServerCount === 0 ? "Connect" : "Manage"}
        onClick={handleCustomMcpClick}
      />
    );
  };

  return (
    <div className="h-full w-full overflow-y-auto custom-scrollbar">
      <div className="px-4 md:px-5 pt-4 pb-2">
        <div className="flex items-center gap-1.5 mb-4">
          <h1 className="text-xl font-semibold text-foreground">Integrations</h1>
          <Tooltip
            content="Connect telemetry, codebase, documents, and other data sources."
            position="right"
            maxWidth="16rem"
          >
            <span className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
              <Info className="h-3.5 w-3.5" />
            </span>
          </Tooltip>
        </div>

        <ReadOnlyIntegrationsBanner />

        <div className="max-w-5xl mb-4 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search integrations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center rounded-md border border-border overflow-hidden">
            <Tooltip content="Grouped by category" position="top">
              <button
                type="button"
                onClick={() => setViewMode("categories")}
                className={cn(
                  "h-9 w-9 flex items-center justify-center transition-colors",
                  viewMode === "categories"
                    ? "bg-accent text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
                )}
                aria-label="Grouped by category"
                aria-pressed={viewMode === "categories"}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
            </Tooltip>
            <Tooltip content="Show all" position="top">
              <button
                type="button"
                onClick={() => setViewMode("all")}
                className={cn(
                  "h-9 w-9 flex items-center justify-center transition-colors",
                  viewMode === "all"
                    ? "bg-accent text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
                )}
                aria-label="Show all"
                aria-pressed={viewMode === "all"}
              >
                <List className="h-4 w-4" />
              </button>
            </Tooltip>
          </div>
        </div>

        {showFlatView ? (
          flatItemCount === 0 ? (
            <p className="text-muted-foreground text-sm max-w-5xl pb-8">
              {isSearching ? `No integrations match "${searchQuery}".` : "No integrations available."}
            </p>
          ) : (
            <div className="max-w-5xl pb-8 space-y-6">
              {connectedItems.length > 0 && (
                <section>
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Connected
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {connectedItems.map(item => renderFlatItem(item))}
                  </div>
                </section>
              )}
              {disconnectedItems.length > 0 && (
                <section>
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                    Not connected
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {disconnectedItems.map(item => renderFlatItem(item))}
                  </div>
                </section>
              )}
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl pb-8">
            {categories.map(category => (
              <IntegrationCategoryCard
                key={category.id}
                category={category}
                connectedTypes={connectedTypes}
                basePath="/settings/integrations"
                variant="settings"
                {...(category.id === "mcp-servers" ? {
                  connectedCountOverride: customMcpServerCount,
                  connectedLabel: `${customMcpServerCount} connected`,
                  manageLabel: "Manage Servers",
                } : {})}
              />
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
